import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

@Entity()
export class ApolloLeadSearchInput extends AdornisEntity {
  static override _class = 'ApolloLeadSearchInput';

  @Field(type => String) icpDescription?: string;
  @Field(type => [String]) companyLocation?: string[];
  @Field(type => [String]) companyIndustry?: string[];
  @Field(type => String) industryKeywords?: string;
  @Field(type => [String]) employeeCount?: string[];
  @Field(type => Int) revenueMin?: number;
  @Field(type => Int) revenueMax?: number;
  @Field(type => String) jobTitle?: string;
  @Field(type => [String]) department?: string[];
  @Field(type => [String]) personLocation?: string[];
  /**
   * Explicit company names to source inside — the strongest constraint the
   * operator can give ("find contacts at exactly these firms"). Drives the
   * organization stage: the org search selects these companies by name and the
   * people search is then scoped to their ids. Absent (or empty) means the
   * search says NOTHING about the company, never that it should match none.
   */
  @Field(type => [String]) organizationNames?: string[];
  @Field(type => Int) page?: number;
  @Field(type => Int) perPage?: number;
  /**
   * When true, the ICP→Apollo mapper skips its persistent cache and asks the
   * LLM for a fresh interpretation (overwriting the cached one). Lets users
   * re-roll the interpretation when they are unhappy with the results.
   */
  @Field(type => Boolean) forceRemap?: boolean;
  /**
   * Apollo person ids to silently drop from the result — used to keep a
   * project's campaigns from re-surfacing leads a sibling campaign already
   * sourced. Plain data, not a query capability: the caller (which has DB
   * access) resolves the actual id list and passes it in here, keeping this
   * package decoupled from the app's own campaign/project schemas.
   */
  @Field(type => [String]) excludeApolloPersonIds?: string[];
}

@Entity()
export class ApolloLeadPreview extends AdornisEntity {
  static override _class = 'ApolloLeadPreview';

  static override resolveID(entity: { apolloPersonId?: string }): string {
    if (!entity.apolloPersonId) throw new Error('ApolloLeadPreview is missing apolloPersonId');
    return entity.apolloPersonId;
  }

  @Field(type => String) apolloPersonId!: string;
  @Field(type => String) firstName!: string;
  @Field(type => String) lastName!: string;
  @Field(type => String) email!: string;
  @Field(type => String) company!: string;
  @Field(type => String) jobTitle!: string;
  @Field(type => String) linkedinUrl?: string;
  @Field(type => String) industry?: string;
  /** Real employee count of the verified company (org-scoped searches only). */
  @Field(type => Int) employeeCount?: number;
  @Field(type => String) lastRefreshedAt?: string;
  @Field(type => Boolean) hasEmail?: boolean;
  @Field(type => String) hasDirectPhone?: string;
}

@Entity()
export class ApolloLeadSearchResult extends AdornisEntity {
  static override _class = 'ApolloLeadSearchResult';

  @Field(type => Int) totalEntries!: number;
  @Field(type => [ApolloLeadPreview]) leads!: ApolloLeadPreview[];
  @Field(type => ApolloAppliedFilters) appliedFilters?: ApolloAppliedFilters;
}

/**
 * The effective Apollo People Search filters a preview request ran with —
 * union of the user's explicit form fields and what the LLM extracted from the
 * Plain English ICP. Surfaced in the UI so users can SEE how their ICP was
 * interpreted (and judge whether a re-interpretation is worth it).
 */
@Entity()
export class ApolloAppliedFilters extends AdornisEntity {
  static override _class = 'ApolloAppliedFilters';

  @Field(type => [String]) personTitles!: string[];
  @Field(type => [String]) personSeniorities!: string[];
  @Field(type => [String]) personLocations!: string[];
  @Field(type => [String]) organizationLocations!: string[];
  /** Company names the search was scoped to, when the operator named any. */
  @Field(type => [String]) organizationNames?: string[];
  @Field(type => [String]) organizationIndustryKeywords!: string[];
  @Field(type => [String]) employeeRanges!: string[];
  /** Departments inferred for the target contacts (UI dropdown values). */
  @Field(type => [String]) departments!: string[];
  /** Effective annual revenue filter in USD (explicit input or ICP-derived). */
  @Field(type => Int) revenueMinUsd?: number;
  @Field(type => Int) revenueMaxUsd?: number;
  @Field(type => String) qKeywords?: string;
  /**
   * True when the ICP→Apollo LLM mapping failed (call error or unparseable
   * output) and the search silently fell back to a raw-text q_keywords match.
   * The UI must surface this — a fallback search returns drastically fewer /
   * worse leads and the user otherwise has no way to tell.
   */
  @Field(type => Boolean) mappingFailed?: boolean;
}
